/**
 * Sign in with a passkey alone: ask the server for a discoverable challenge, let the browser pick a passkey, and
 * send the assertion back. Used both by the username field's autofill (conditional mediation, which waits quietly
 * until the guest picks a passkey from the list) and by the "Sign in with a passkey" button.
 *
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add signInWithPasskey().
 *
 * 2026-10-26 (0.1.1) (jhoward)
 *   - Treat a prompt called off through the signal as aborted, whatever it rejects with. Login.vue aborts with a
 *     string reason, which the browser rejects with as-is; that was not an AbortError, so signing in with a password
 *     showed "We couldn't verify your passkey" to a guest who never touched one. Also stop before opening the prompt
 *     if it was called off while the challenge was being fetched.
 *   - The autofill (conditional) prompt fails quietly when the browser will not open it, e.g. a SecurityError on a
 *     property domain whose related-origin check failed. It opens with the page, so its toast greeted guests who
 *     had not tried a passkey at all. The button still says what went wrong.
 */

import type { FetchResponse } from '@framework/defs/framework';
import { arrayBufferToBase64, recursiveBase64StrToArrayBuffer } from '@framework/helpers';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import { describeWebAuthnError } from '@/helpers/webAuthnSupport';
import type { LoginResponseData } from '@/views/Auth/Login.vue';
import { challenge, verify } from '@rss/routes/login/passkey';

export interface PasskeySignInOptions {
  mediation: 'conditional' | 'optional';
  remember: boolean;
  signal?: AbortSignal;
}

export interface PasskeySignInResult {
  success: boolean;
  //The prompt was closed by code (page left, form submitted) rather than by the guest; nothing to say.
  aborted: boolean;
  message: string;
  data?: LoginResponseData;
}

type ChallengeResponse = FetchResponse & { data?: CredentialRequestOptions };

interface VerifyResponse extends FetchResponse {
  data?: LoginResponseData;
}

export default async function signInWithPasskey(options: PasskeySignInOptions): Promise<PasskeySignInResult> {
  const quiet = 'conditional' === options.mediation;

  const challengeResponse = await peppermillCustomFetchProxy<ChallengeResponse>(
    {},
    { route: challenge(), showLoadingSpinner: !quiet },
  );
  //Called off while the challenge was on its way, e.g. the guest signed in with a password meanwhile.
  if (options.signal?.aborted) {
    return { success: false, aborted: true, message: '' };
  }
  if (200 !== challengeResponse.status || !challengeResponse.data?.publicKey) {
    return { success: false, aborted: false, message: challengeResponse.message || 'Could not start the passkey prompt.' };
  }

  const requestOptions: CredentialRequestOptions = {
    publicKey: challengeResponse.data.publicKey,
    mediation: options.mediation,
    signal: options.signal,
  };
  recursiveBase64StrToArrayBuffer(requestOptions);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get(requestOptions)) as PublicKeyCredential | null;
  } catch (err) {
    //abort(reason) rejects with the reason itself - Login.vue passes a string - not an AbortError, so the signal is
    //what says whether this was called off rather than failed.
    const aborted = true === options.signal?.aborted || (err instanceof Error && 'AbortError' === err.name);

    //The autofill prompt starts with the page, not with anything the guest did. If it cannot start, the password
    //form is still there; telling them a passkey failed when they never reached for one only confuses.
    if (quiet && !aborted) {
      console.warn('Passkey autofill could not start.', err);

      return { success: false, aborted: true, message: '' };
    }

    return { success: false, aborted, message: aborted ? '' : describeWebAuthnError(err, 'get') };
  }

  if (!credential) {
    return { success: false, aborted: false, message: 'No passkey was used.' };
  }

  const assertion = credential.response as AuthenticatorAssertionResponse;
  const verifyResponse = await peppermillCustomFetchProxy<VerifyResponse>(
    {
      id: arrayBufferToBase64(credential.rawId),
      clientDataJSON: arrayBufferToBase64(assertion.clientDataJSON),
      authenticatorData: arrayBufferToBase64(assertion.authenticatorData),
      signature: arrayBufferToBase64(assertion.signature),
      userHandle: assertion.userHandle ? arrayBufferToBase64(assertion.userHandle) : null,
      remember: options.remember ? 1 : 0,
      //One choice covers both: a guest who wants to stay signed in on this device wants it trusted for sign-in too.
      remember_device: options.remember ? 1 : 0,
    },
    { route: verify() },
  );

  return {
    success: 200 === verifyResponse.status,
    aborted: false,
    message: verifyResponse.message || (200 === verifyResponse.status ? 'Signed in with your passkey.' : "We couldn't verify your passkey. Try again."),
    data: verifyResponse.data,
  };
}
